/* eslint-disable max-len */
import React, {type RefObject} from 'react';

import {keyframes} from '@emotion/react';
import styled from '@emotion/styled';

/**
 * The `LoaderProps` interface defines the shape of the properties object that is expected for this component.
 * It outlines the required properties that needs to be provided when utilizing this component expecting an object of this type.
 */
interface LoaderProps {
    /**
     * A string representing the CSS class to be applied to the LoaderIcon element to override its default styling.
     *
     * @default ''
     */
    className?: string;
    /**
     * A string representing the color to be applied.
     *
     * @default '#fff'
     */
    color1?: string;
    /**
     * A number or string representing the height of the LoaderIcon element.
     *
     * @default 29
     */
    height?: number | string;
    /**
     * The `ref` property represents a reference to the SVG element, allowing for direct manipulation or access to the DOM node.
     */
    ref?: RefObject<SVGSVGElement | null>;
    /**
     * The `testId` property represents a unique identifier, usually in the form of a string, assigned to a component for testing purposes.
     * This property is crucial for uniquely identifying components during testing, allowing for more accurate and reliable tests.
     *
     * @default 'Loader'
     */
    testId?: string;
    /**
     * A number or string representing the height of the LoaderIcon element.
     *
     * @default 29
     */
    width?: number | string;
}

/**
 * The `Loader` component is a functional component designed to render a customizable SVG element representing an Icon.
 * It receives several props through the `LoaderProps` interface, allowing for customization of class, color, dimensions, and testing identifier.
 *
 * @param props           The component props.
 * @param props.className A string representing the CSS class to be applied to the LoaderIcon element to override its default styling.
 * @param props.color1    A string representing the color to be applied to the Icon.
 * @param props.height    A number or string representing the height of the LoaderIcon element.
 * @param props.testId    A string representing a unique identifier assigned to the component for testing purposes.
 * @param props.width     A number or string representing the height of the LoaderIcon element.
 * @param props.ref       A reference to the SVG element, allowing for direct manipulation or access to the DOM node.
 * @returns A React element representing the `Check` component with the specified properties.
 *
 * @example
 * ```tsx
 * const LoaderComponent = <Loader className="myClass" color1="#000" height="20" testId="myTestId" width="20" />;
 * ```
 */
export const Loader = ({className = '', color1 = '#fff', height = 29, ref, testId = 'Loader', width = 29}: LoaderProps) => (
    <Svg
        ref={ref}
        className={className}
        data-cy={testId}
        height={height}
        viewBox="0 0 29 29"
        width={width}
        xmlns="http://www.w3.org/2000/svg"
        xmlnsXlink="http://www.w3.org/1999/xlink"
    >
        <path d="M14.5 2.42v4.83m0 14.5v4.83M5.96 5.96l3.42 3.42m10.24 10.24 3.42 3.42M2.42 14.5h4.83m14.5 0h4.83M5.96 23.04l3.42-3.42M19.62 9.38l3.42-3.42" stroke={color1} strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.67" />
    </Svg>
);

Loader.displayName = 'Loader';

const spin = keyframes`
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
`;

const Svg = styled.svg`
    animation: ${spin} 2s linear infinite;
`;